import { FC, useState } from 'react';
// Merge button intentionally uses raw antd Button to preserve the red filled
// `danger` styling — Base/Button's variant system has no equivalent.
import { Button as AntdButton } from 'antd';
import Button from 'components/Base/Button/Button';
import { Modal } from 'components/Base/Modal';
import Spinner from 'components/Base/Spinner';
import { Tooltip } from 'components/Base/Tooltip';
import { Alert } from 'components/Base/Alert';
import { ENotificationType, useShowToastNotification } from '@packages/utils';
import { useGetPeopleByIdQuery } from 'redux/apiSlice/people.slice';
import { useGetOrganizationByIdQuery } from 'redux/apiSlice/organizations.slice';
import {
  useDismissDuplicatePairMutation,
  useMergeRecordsMutation,
  useSwapDuplicatePairMutation,
} from 'redux/apiSlice/duplicateManagement.slice';
import {
  EDuplicateEntityType,
  EFailureReasonCode,
  EMergeStatus,
  TDuplicatePairUI,
} from 'data/types/duplicateManagement.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { SActionBar, SExpandedRow } from '../../DuplicateMerge.styles';
import CompareCards from './CompareCards';
import MatchCriteriaSummary from './MatchCriteriaSummary';
import MergeConfirmModal from '../MergeConfirmModal';

type Props = {
  pair: TDuplicatePairUI;
};

const failureCopy: Record<EFailureReasonCode, string> = {
  [EFailureReasonCode.UNIQUE_CONFLICT]:
    'A unique-field collision was detected (e.g. email or phone) between the primary and secondary records. Resolve the conflict on the primary record before merging.',
  [EFailureReasonCode.VERSION_STALE]:
    'One of the records was modified by another user. Refresh and try again.',
  [EFailureReasonCode.INTERNAL_ERROR]:
    'An unexpected error occurred. Please try again.',
};

const DuplicatePairCompareRow: FC<Props> = ({ pair }) => {
  const canMerge = useCheckPermission('DUPLICATE_MANAGEMENT_MERGE');
  const canDismiss = useCheckPermission('DUPLICATE_MANAGEMENT_DISMISS');
  const canSwap = useCheckPermission('DUPLICATE_MANAGEMENT_SWAP');
  const { showToastNotification } = useShowToastNotification();

  const isPeople = pair.entityType === EDuplicateEntityType.PEOPLE;

  const peopleA = useGetPeopleByIdQuery(
    { userId: pair.recordAId },
    { skip: !isPeople },
  );
  const peopleB = useGetPeopleByIdQuery(
    { userId: pair.recordBId },
    { skip: !isPeople },
  );
  const orgA = useGetOrganizationByIdQuery(
    { id: pair.recordAId },
    { skip: isPeople },
  );
  const orgB = useGetOrganizationByIdQuery(
    { id: pair.recordBId },
    { skip: isPeople },
  );

  const isLoading = isPeople
    ? peopleA.isLoading || peopleB.isLoading
    : orgA.isLoading || orgB.isLoading;

  const fetchError = isPeople
    ? peopleA.error || peopleB.error
    : orgA.error || orgB.error;

  const [mergeOpen, setMergeOpen] = useState(false);
  const [errorReason, setErrorReason] = useState<EFailureReasonCode | null>(
    null,
  );

  const [mergeRecords, { isLoading: merging }] = useMergeRecordsMutation();
  const [dismissPair, { isLoading: dismissing }] =
    useDismissDuplicatePairMutation();
  const [swapPair, { isLoading: swapping }] = useSwapDuplicatePairMutation();

  const secondaryLabel = isPeople
    ? `${peopleB.data?.firstName?.value ?? ''} ${
        peopleB.data?.lastName?.value ?? ''
      }`.trim() || 'this record'
    : orgB.data?.name?.value || 'this record';

  const handleSwap = async () => {
    try {
      await swapPair({ duplicatePairId: pair.id }).unwrap();
      showToastNotification({
        message: 'Records swapped',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Could not swap records',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissPair({ duplicatePairId: pair.id }).unwrap();
      showToastNotification({
        message: 'Marked as Not Duplicates',
        type: ENotificationType.SUCCESS,
      });
    } catch {
      showToastNotification({
        message: 'Could not dismiss',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleMergeConfirm = async () => {
    try {
      const response = await mergeRecords({
        entityType: pair.entityType,
        primaryRecordId: pair.recordAId,
        secondaryRecordIds: [pair.recordBId],
      }).unwrap();
      if (response.status === EMergeStatus.SUCCESS) {
        showToastNotification({
          message: `${secondaryLabel} was merged into the primary record.`,
          type: ENotificationType.SUCCESS,
        });
        setMergeOpen(false);
        return;
      }
      // FAILED — surface failure reason via error modal.
      setMergeOpen(false);
      setErrorReason(
        // BE returns no failureReasonCode on the merge response itself —
        // history rows carry it. Fall back to INTERNAL_ERROR copy.
        EFailureReasonCode.INTERNAL_ERROR,
      );
    } catch {
      setMergeOpen(false);
      setErrorReason(EFailureReasonCode.INTERNAL_ERROR);
    }
  };

  if (isLoading) {
    return (
      <SExpandedRow>
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spinner spinnerSize={20} /> Loading record details…
        </div>
      </SExpandedRow>
    );
  }

  if (fetchError) {
    return (
      <SExpandedRow>
        <Alert
          type="error"
          message="Could not load one of the records."
          description="The record may have been deleted. Try refreshing the list."
        />
      </SExpandedRow>
    );
  }

  // Type-narrowed render. We've already short-circuited on isLoading/error.
  let cardsNode = null;
  if (isPeople && peopleA.data && peopleB.data) {
    cardsNode = (
      <CompareCards
        entityType={EDuplicateEntityType.PEOPLE}
        primary={peopleA.data}
        secondary={peopleB.data}
        mode="willMerge"
      />
    );
  } else if (!isPeople && orgA.data && orgB.data) {
    cardsNode = (
      <CompareCards
        entityType={EDuplicateEntityType.ORGANIZATION}
        primary={orgA.data}
        secondary={orgB.data}
        mode="willMerge"
      />
    );
  }

  return (
    <SExpandedRow>
      <MatchCriteriaSummary matchCriteria={pair.matchCriteria} />
      {cardsNode}
      <SActionBar>
        <Tooltip
          title={canSwap ? '' : 'Requires DUPLICATE_MANAGEMENT_SWAP permission'}
        >
          <Button onClick={handleSwap} disabled={!canSwap} loading={swapping}>
            Swap
          </Button>
        </Tooltip>
        <Tooltip
          title={
            canDismiss ? '' : 'Requires DUPLICATE_MANAGEMENT_DISMISS permission'
          }
        >
          <Button
            disabled={!canDismiss}
            loading={dismissing}
            onClick={handleDismiss}
          >
            Not Duplicates
          </Button>
        </Tooltip>
        <Tooltip
          title={
            canMerge ? '' : 'Requires DUPLICATE_MANAGEMENT_MERGE permission'
          }
        >
          <AntdButton
            type="primary"
            danger
            disabled={!canMerge}
            onClick={() => setMergeOpen(true)}
          >
            Merge
          </AntdButton>
        </Tooltip>
      </SActionBar>

      <MergeConfirmModal
        open={mergeOpen}
        loading={merging}
        entityToBeMergedLabel={secondaryLabel}
        onCancel={() => setMergeOpen(false)}
        onConfirm={handleMergeConfirm}
      />

      <Modal
        open={errorReason !== null}
        title="Merge failed"
        onCancel={() => setErrorReason(null)}
        footer={[
          <Button
            key="close"
            variant="default"
            onClick={() => setErrorReason(null)}
          >
            Close
          </Button>,
          <Button
            key="retry"
            variant="primary"
            onClick={() => {
              setErrorReason(null);
              setMergeOpen(true);
            }}
          >
            Retry
          </Button>,
        ]}
      >
        <p>
          <strong>Reason:</strong> {errorReason ?? 'INTERNAL_ERROR'}
        </p>
        {errorReason && <p>{failureCopy[errorReason]}</p>}
      </Modal>
    </SExpandedRow>
  );
};

export default DuplicatePairCompareRow;
